import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { addToCart, removeFromCart } from '../actions/cartActions';
import Remove from '../assets/svg/remove.svg?react';
import MessageBox from '../components/MessageBox';
import PlaceHolder from '../components/Placeholder';
import { useLazyLoad } from '../hooks/useLazyLoad';
import { scrollTop, sizes, toPrice } from '../utils';
import { notyf } from '../utils/notyf';

function CartItemImage({ item }) {
  const [containerRef, inView] = useLazyLoad('300px');
  const imgRef = useRef(null);

  useEffect(() => {
    if (inView && imgRef.current?.complete) {
      document.getElementById(`${item.product}-cart-img`)?.classList.add('show');
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={containerRef} className="item-image">
      <PlaceHolder height="100%" hide={inView}>
        <div id={`${item.product}-cart-img`} className="item-image-inner">
          <Link to={`/shop/product/${item.product}`}>
            <img
              ref={imgRef}
              className="small"
              src={inView ? item.image : undefined}
              alt={item.name}
              onLoad={() =>
                document.getElementById(`${item.product}-cart-img`)?.classList.add('show')
              }
            />
          </Link>
        </div>
      </PlaceHolder>
    </div>
  );
}

export default function CartScreen(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const sizeOption = (size, val) => {
    return (
      val > 0 && (
        <option key={size} value={size}>
          {size}
        </option>
      )
    );
  };

  const qtyOption = (val) => {
    return [...Array(val >= 5 ? 5 : val).keys()].map((x) => (
      <option key={x + 1} value={x + 1}>
        {x + 1}
      </option>
    ));
  };

  const changeSize = (id, size) => {
    dispatch(addToCart(id, 1, String(size)));
    notyf.success({
      icon: false,
      message: t('cart.sizeUpdated'),
      dismissible: true,
    });
  };

  const changeQty = (id, qty, size) => {
    dispatch(addToCart(id, Number(qty), size));
    notyf.success({
      icon: false,
      message: t('cart.qtyUpdated'),
      dismissible: true,
    });
  };

  const undoFnRef = useRef(null);

  const removeFromCartHandler = (item) => {
    dispatch(removeFromCart(item.product, item.name));
    undoFnRef.current = () => {
      dispatch(addToCart(item.product, item.qty, item.size));
      notyf.dismissAll();
    };
    window.__cartUndo = () => undoFnRef.current?.();
    notyf.error({
      icon: false,
      message: `"${item.name}" ${t('cart.removedFromCart')} &nbsp;<span class="notyf-undo" onclick="window.__cartUndo()">${t('cart.undo')}</span>`,
      dismissible: true,
      duration: 5000,
    });
  };

  cart.itemsQty = cartItems.reduce((a, c) => a + c.qty, 0);
  cart.itemsPrice = toPrice(cartItems.reduce((a, c) => a + c.price * c.qty, 0));
  cart.shippingPrice = cart.itemsPrice >= 40 ? toPrice(0) : toPrice(9.99);
  cart.totalPrice = cart.itemsPrice + cart.shippingPrice;

  const checkoutHandler = () => {
    navigate('/cart/shipping');
  };

  useEffect(() => {
    scrollTop();
  }, []);

  return (
    <section className="cart">
      <h1 className="custom-font">{t('cart.title')}</h1>
      {cartItems.length === 0 ? (
        <div>
          <MessageBox>
            {t('cart.empty')} <Link to="/shop">{t('cart.emptyLink')}</Link> {t('cart.emptyShop')}
          </MessageBox>
        </div>
      ) : (
        <>
          <ul className="cart-items">
            {cartItems.map((item) => (
              <li key={item.product}>
                <CartItemImage item={item} />
                <div className="item-content">
                  <div className="item-name">
                    <h3>{item.name}</h3>
                  </div>
                  <div className="item-price">
                    {item.originalPrice && item.originalPrice > item.price && (
                      <p className="item-price-original">{item.originalPrice.toFixed(2)}€</p>
                    )}
                    <h3>{item.price && item.price.toFixed(2)}€</h3>
                  </div>
                </div>
                <div className="item-content">
                  {item.isClothing && (
                    <div className="item-size">
                      <span>{t('cart.size')}:</span>{' '}
                      <select
                        value={item.size}
                        onChange={(e) => changeSize(item.product, e.target.value)}
                      >
                        {sizes.map((size) =>
                          size === 'xs'
                            ? sizeOption(size, item.countInStock.xs)
                            : size === 's'
                              ? sizeOption(size, item.countInStock.s)
                              : size === 'm'
                                ? sizeOption(size, item.countInStock.m)
                                : size === 'l'
                                  ? sizeOption(size, item.countInStock.l)
                                  : size === 'xl'
                                    ? sizeOption(size, item.countInStock.xl)
                                    : size === 'xxl' && sizeOption(size, item.countInStock.xxl)
                        )}
                      </select>
                    </div>
                  )}
                  <div className="item-qty">
                    <span>{t('cart.quantity')}:</span>{' '}
                    <select
                      value={item.qty}
                      onChange={(e) => changeQty(item.product, e.target.value, item.size)}
                    >
                      {!item.isClothing
                        ? qtyOption(item.countInStock.stock)
                        : item.size === 'xs'
                          ? qtyOption(item.countInStock.xs)
                          : item.size === 's'
                            ? qtyOption(item.countInStock.s)
                            : item.size === 'm'
                              ? qtyOption(item.countInStock.m)
                              : item.size === 'l'
                                ? qtyOption(item.countInStock.l)
                                : item.size === 'xl'
                                  ? qtyOption(item.countInStock.xl)
                                  : item.size === 'xxl' && qtyOption(item.countInStock.xxl)}
                    </select>
                  </div>
                  <div className="item-remove">
                    <Remove className="icon" onClick={() => removeFromCartHandler(item)} />
                    <button className="secondary" onClick={() => removeFromCartHandler(item)}>
                      {t('cart.remove')}
                    </button>
                  </div>
                </div>
                <div className="line"></div>
              </li>
            ))}
          </ul>
          <ul className="cart-total">
            <li>
              {t('cart.subtotal', { count: cart.itemsQty })} :{' '}
              {cart.itemsPrice && cart.itemsPrice.toFixed(2)}€
            </li>
            <li>
              <h2>
                {t('cart.total')} : {cart.itemsPrice && cart.itemsPrice.toFixed(2)}€
              </h2>
            </li>
            <li>
              <button className="primary" onClick={checkoutHandler}>
                {t('cart.checkout')}
              </button>
            </li>
          </ul>
        </>
      )}
    </section>
  );
}
